import {
  Configuration,
  type Middleware,
} from "@exagens/behavioral-insight-console-client/dist/runtime";
import { getConsoleAccessToken } from "@/lib/auth/consoleAccessToken";
import {
  csrfCustomHeaderRecord,
  isMutatingHttpMethod,
} from "@/lib/http/csrfCustomHeader";

type OpenApiRequestArgs = Parameters<NonNullable<Middleware["pre"]>>[0];

/**
 * OpenAPI `pre` middleware: Bearer token (mirrored synchronously from the OIDC
 * session) on every call, plus the anti-CSRF header on mutating methods.
 */
export const applyConsoleApiBearerMiddleware = (
  req: OpenApiRequestArgs,
): OpenApiRequestArgs => {
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(req.headers ?? {})) {
    if (name.toLowerCase() !== "authorization") {
      headers[name] = String(value);
    }
  }
  const token = getConsoleAccessToken();
  if (token != null) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (isMutatingHttpMethod(req.method)) {
    Object.assign(headers, csrfCustomHeaderRecord());
  }
  return { ...req, headers };
};

const consoleApiBearer: Middleware = { pre: applyConsoleApiBearerMiddleware };

const sparxsEditorPlainTextSuccessResponse: Middleware = {
  pre: (req) => {
    if (req.method !== "POST" || typeof req.url !== "string") {
      return req;
    }
    if (
      req.url.includes("/api/sparxseditor/save") ||
      req.url.includes("/api/sparxseditor/discard")
    ) {
      return { ...req, responseType: "text" as const };
    }
    return req;
  },
};

let consoleApiBasePath: string | null = null;

const requireConsoleApiBasePath = (): string => {
  if (consoleApiBasePath === null) {
    throw new Error(
      "OpenAPI client is not initialized; call initOpenApiClientRuntime first",
    );
  }
  return consoleApiBasePath;
};

export const createSparxEditorApiConfiguration = (): Configuration =>
  new Configuration({
    basePath: requireConsoleApiBasePath(),
    middleware: [consoleApiBearer, sparxsEditorPlainTextSuccessResponse],
  });

let configuration: Configuration | null = null;

/** `basePath` is the console service origin from runtime config (tests/stories: same origin). */
export const initOpenApiClientRuntime = (
  basePath: string = window.location.origin,
): void => {
  consoleApiBasePath = basePath;
  configuration = new Configuration({
    basePath,
    middleware: [consoleApiBearer],
  });
};

export const getOpenApiConfiguration = (): Configuration => {
  if (configuration === null) {
    throw new Error(
      "OpenAPI client is not initialized; call initOpenApiClientRuntime first",
    );
  }
  return configuration;
};
